import { Suspense, lazy, type ReactElement } from 'react';
import { Routes, Route, useMatch } from 'react-router';
import { Analytics } from '@vercel/analytics/react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './views/Home';
import Search from './components/Search';
import PrivateRoute from './components/PrivateRoute';
import AnonRoute from './components/AnonRoute';
import AuthProvider from './lib/AuthProvider';
import { loadRecipes, loadRecipeDetail } from './lib/prefetch';

/*
 * Home is the landing route, so it ships in the main bundle and paints without a
 * chunk round-trip. Every other view is code-split with React.lazy — its markup
 * is fetched only when that route is visited, keeping the initial JS small. In a
 * CRA SPA the chrome (Navbar/Footer) and views are all client-rendered React;
 * splitting the non-landing routes is the meaningful "not all markup needs to
 * load up front" win available without a static-rendering framework.
 */
const Recipes = lazy(loadRecipes);
const RecipeCreate = lazy(() => import('./views/RecipeCreate'));
const RecipeImport = lazy(() => import('./views/RecipeImport'));
const RecipeDetailWrapper = lazy(loadRecipeDetail);
const PantryPage = lazy(() => import('./views/PantryPage'));
const Pricing = lazy(() => import('./views/Pricing'));
const Profile = lazy(() => import('./views/Profile'));
const AuthorProfile = lazy(() => import('./views/AuthorProfile'));
const Signup = lazy(() => import('./views/Signup'));
const Login = lazy(() => import('./views/Login'));
const OAuthAuthorize = lazy(() => import('./views/OAuthAuthorize'));
const CookMode = lazy(() => import('./views/CookMode'));
const ShoppingList = lazy(() => import('./views/ShoppingList'));
const RecipeEdit = lazy(() => import('./views/RecipeEdit'));
const Household = lazy(() => import('./views/Household'));
const HouseholdJoin = lazy(() => import('./views/HouseholdJoin'));

function RouteFallback(): ReactElement {
	return <div className='route-fallback' role='status' aria-label='Loading' />;
}

function App(): ReactElement {
	// Cook mode is its own surface: the site chrome would only take room from
	// the steps, and its 40px controls are below the 72px kitchen floor.
	const cooking = useMatch('/recipes/:id/cook') !== null;

	return (
		<AuthProvider>
			<a className='skip-link' href='#main-content'>Skip to content</a>
			{!cooking && <Navbar />}
			<main id='main-content' tabIndex={-1}>
				<Suspense fallback={<RouteFallback />}>
					<Routes>
						<Route path='/recipes/create' element={<PrivateRoute><RecipeCreate /></PrivateRoute>} />
						<Route path='/recipes/import' element={<PrivateRoute><RecipeImport /></PrivateRoute>} />
						<Route path='/recipes/:id/cook' element={<CookMode />} />
						<Route path='/recipes/:id/edit' element={<PrivateRoute><RecipeEdit /></PrivateRoute>} />
						<Route path='/recipes/:id' element={<RecipeDetailWrapper />} />
						<Route path='/signup' element={<AnonRoute><Signup /></AnonRoute>} />
						<Route path='/login' element={<AnonRoute><Login /></AnonRoute>} />
						<Route path='/profile' element={<PrivateRoute><Profile /></PrivateRoute>} />
						<Route path='/users/:id' element={<AuthorProfile />} />
						<Route path='/pantry' element={<PrivateRoute><PantryPage /></PrivateRoute>} />
						<Route path='/shopping-list' element={<PrivateRoute><ShoppingList /></PrivateRoute>} />
						<Route path='/household' element={<PrivateRoute><Household /></PrivateRoute>} />
						<Route path='/household/join/:token' element={<HouseholdJoin />} />
						<Route path='/oauth/authorize' element={<OAuthAuthorize />} />
						<Route path='/pricing' element={<Pricing />} />
						<Route path='/recipes' element={<Recipes />} />
						<Route path='/search' element={<Search />} />
						<Route path='/' element={<Home />} />
					</Routes>
				</Suspense>
			</main>
			{!cooking && <Footer />}
			<Analytics />
		</AuthProvider>
	);
}

export default App;
